import React, { useState, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { RECAPTCHA_SITE_KEY } from "../../constants/config";
import { makePostRequest } from "../../services/httpHelper";
import ErrorHandler from "../../services/ErrorHandler";
import { isEmail, isEmpty, isMobile } from "../../utils/formValidator";
import { CircularProgress } from "@material-ui/core";

const ContactForm = () => {
  const recaptchaRef = useRef();
  const [values, setValues] = useState({
    contactName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);

  const handleChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const handleRecaptcha = () => {
    setIsCaptchaVerified(true);
  };

  const validate = () => {
    let tempErrors = {};

    if (isEmpty(values.contactName)) tempErrors.contactName = "Enter your full name";
    else if (values.contactName.length < 5) tempErrors.contactName = "Name is too short";

    if (isEmpty(values.email)) tempErrors.email = "Enter your email address";
    else if (!isEmail(values.email)) tempErrors.email = "Invalid email address";

    if (isEmpty(values.phone)) tempErrors.phone = "Enter your mobile number";
    else if (!isMobile(values.phone)) tempErrors.phone = "Invalid mobile number";

    if (isEmpty(values.subject)) tempErrors.subject = "Please enter subject";

    if (isEmpty(values.message)) tempErrors.message = "Enter your message";
    else if (values.message.length < 10) tempErrors.message = "Message is too short";

    if (!isCaptchaVerified) tempErrors.captcha = "Please verify that you are not a robot";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError(null);

    if (!validate()) return;

    setIsSubmitting(true);

    const data = { ...values };

    try {
      const res = await makePostRequest("contact-us", data);
      if (res && res.errorType) {
        console.log('Contact response:',res)
        setServerError(ErrorHandler(res.errorMessage.toString()));
        setIsSubmitting(false);
      } else {
        setSubmitted(true);
        setIsSubmitting(false);
        setValues({ contactName: "", email: "", phone: "", subject: "", message: "" });
        recaptchaRef.current.reset();
        setIsCaptchaVerified(false);
      }
    } catch (err) {
      setServerError("An unexpected error occurred. Please try again later.");
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-white py-16">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-4xl font-bold text-purple-900 mb-4">Let's get in touch</h2>
        <p className="text-purple-800 mb-8">
          Have questions or need assistance? Our team is here to help. Contact us today.
        </p>

        {submitted && (
          <p className="text-green-600 mb-6">
            Thank you for contacting us {values.contactName}. We shall get back to you within 24 hours.
          </p>
        )}

        {serverError && <p className="text-red-600 mb-6">{serverError}</p>}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block mb-2 font-semibold text-purple-900">Full Name</label>
            <input
              type="text"
              name="contactName"
              value={values.contactName}
              onChange={handleChange}
              className="w-full px-4 py-2 border-b-2 border-purple-300 focus:border-orange-500 outline-none text-black"
            />
            {errors.contactName && <p className="text-red-600 mt-1">{errors.contactName}</p>}
          </div>

          <div>
            <label className="block mb-2 font-semibold text-purple-900">Email</label>
            <input
              type="email"
              name="email"
              value={values.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border-b-2 border-purple-300 focus:border-orange-500 outline-none text-black"
            />
            {errors.email && <p className="text-red-600 mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block mb-2 font-semibold text-purple-900">Mobile Phone</label>
            <input
              type="text"
              name="phone"
              value={values.phone}
              onChange={handleChange}
              className="w-full px-4 py-2 border-b-2 border-purple-300 focus:border-orange-500 outline-none text-black"
            />
            {errors.phone && <p className="text-red-600 mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label className="block mb-2 font-semibold text-purple-900">Subject</label>
            <input
              type="text"
              name="subject"
              value={values.subject}
              onChange={handleChange}
              className="w-full px-4 py-2 border-b-2 border-purple-300 focus:border-orange-500 outline-none text-black"
            />
            {errors.subject && <p className="text-red-600 mt-1">{errors.subject}</p>}
          </div>

          <div>
            <label className="block mb-2 font-semibold text-purple-900">Message</label>
            <textarea
              name="message"
              rows="5"
              value={values.message}
              onChange={handleChange}
              className="w-full px-4 py-2 border-b-2 border-purple-300 focus:border-orange-500 outline-none text-black"
            />
            {errors.message && <p className="text-red-600 mt-1">{errors.message}</p>}
          </div>

          <div>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={handleRecaptcha}
            />
            {errors.captcha && <p className="text-red-600 mt-1">{errors.captcha}</p>}
          </div>

          

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-purple-900 hover:bg-orange-500 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting && <CircularProgress size={20} className="text-white" />}
              Send Message
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
